import React, { useEffect, useRef, useState } from "react";
import { FaMinus, FaPlus } from "react-icons/fa";
import { IconButton } from "../../UI/DesignStandards";
import { parseSheetBox } from "../../../utility/sheetHighlight";
import { findNameLineBox } from "../../../utility/pdfNameHighlight";
import { unionHighlightBoxes } from "../../../utility/recapSheets/recapTeamBox";

const PDFJS_VERSION = "3.11.174";
const MIN_ZOOM = 0.75;
const MAX_ZOOM = 8;
const zoomByPage = new Map();
const panByPage = new Map();

export const isPdfFile = (mimeType, fileName) =>
  (mimeType || "").includes("pdf") || /\.pdf$/i.test(fileName || "");

export const loadPdfJs = () =>
  new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.min.js`;
    script.async = true;
    script.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error("Could not load the PDF viewer."));
        return;
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`;
      resolve(window.pdfjsLib);
    };
    script.onerror = () =>
      reject(new Error("Could not load the PDF viewer."));
    document.head.appendChild(script);
  });

const pageZoomKey = (storagePath, pageNumber) =>
  `${storagePath || ""}|${pageNumber}`;

const clampZoom = (value) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value.toFixed(2))));

const readPan = (pane) => {
  if (!pane || pane.scrollWidth <= 0 || pane.scrollHeight <= 0) return null;
  return {
    x: (pane.scrollLeft + pane.clientWidth / 2) / pane.scrollWidth,
    y: (pane.scrollTop + pane.clientHeight / 2) / pane.scrollHeight,
  };
};

const applyPan = (pane, pan) => {
  if (!pane || !pan) return;
  pane.scrollLeft = pan.x * pane.scrollWidth - pane.clientWidth / 2;
  pane.scrollTop = pan.y * pane.scrollHeight - pane.clientHeight / 2;
};

const savePan = (key, pane) => {
  const pan = readPan(pane);
  if (pan) panByPage.set(key, pan);
};

const ZoomBar = ({ zoom, onZoomOut, onZoomIn, onReset, toolbarLeft }) => (
  <div className="flex items-center gap-3 mb-3 shrink-0">
    {toolbarLeft ? <div className="min-w-0 flex-1">{toolbarLeft}</div> : null}
    <div className="flex items-center gap-1 shrink-0">
      <IconButton
        type="button"
        icon={FaMinus}
        variant="secondary"
        size="sm"
        onClick={onZoomOut}
        disabled={zoom <= MIN_ZOOM}
        aria-label="Zoom out"
      />
      <span className="w-12 text-center text-sm text-brand-charcoal tabular-nums">
        {Math.round(zoom * 100)}%
      </span>
      <IconButton
        type="button"
        icon={FaPlus}
        variant="secondary"
        size="sm"
        onClick={onZoomIn}
        disabled={zoom >= MAX_ZOOM}
        aria-label="Zoom in"
      />
      {zoom !== 1 ? (
        <button
          type="button"
          onClick={onReset}
          className="ml-1 px-2 py-1 text-xs font-display font-bold text-brand-charcoal hover:text-brand-gold-ink"
        >
          100%
        </button>
      ) : null}
    </div>
  </div>
);

const pageSizeFromSource = (source) => ({
  width: source.naturalWidth || source.width,
  height: source.naturalHeight || source.height,
});

const HighlightBox = ({ box, pageSize, width, height, paintHighlight }) => {
  const parsed = parseSheetBox(box, pageSize);
  if (!parsed || !width || !height) return null;
  return (
    <div
      data-sheet-highlight="true"
      className="absolute z-10 pointer-events-none"
      style={{
        left: parsed.topLeftX * width,
        top: parsed.topLeftY * height,
        width: (parsed.bottomRightX - parsed.topLeftX) * width,
        height: (parsed.bottomRightY - parsed.topLeftY) * height,
        background: "none",
        backgroundColor: "transparent",
        backgroundImage: "none",
        boxShadow: "none",
        outline: "none",
        border: paintHighlight
          ? "1px solid rgba(26, 26, 26, 0.18)"
          : "2px solid transparent",
      }}
    />
  );
};

const drawCrop = (source, paneWidth, paneHeight, crop) => {
  const width = source.naturalWidth || source.width;
  const height = source.naturalHeight || source.height;
  if (!width || !height || !crop) return null;
  const sx = crop.topLeftX * width;
  const sy = crop.topLeftY * height;
  const sw = Math.max(1, (crop.bottomRightX - crop.topLeftX) * width);
  const sh = Math.max(1, (crop.bottomRightY - crop.topLeftY) * height);
  const maxW = Math.max(paneWidth || 640, 200);
  const maxH = Math.max(paneHeight || 160, 96);
  const scale = Math.min(maxW / sw, maxH / sh);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(sw * scale));
  canvas.height = Math.max(1, Math.round(sh * scale));
  const context = canvas.getContext("2d");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(source, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const expandCropBox = (parsed) => {
  if (!parsed) return null;
  const width = parsed.bottomRightX - parsed.topLeftX;
  const height = parsed.bottomRightY - parsed.topLeftY;
  const padX = Math.min(0.04, Math.max(width * 0.04, 0.02));
  const padY = Math.min(0.03, Math.max(height * 0.2, 0.012));
  return {
    topLeftX: Math.max(0, parsed.topLeftX - padX),
    topLeftY: Math.max(0, parsed.topLeftY - padY),
    bottomRightX: Math.min(1, parsed.bottomRightX + padX),
    bottomRightY: Math.min(1, parsed.bottomRightY + padY),
  };
};

const drawPage = (source, targetWidth) => {
  const width = source.naturalWidth || source.width;
  const height = source.naturalHeight || source.height;
  if (!width || !height) return null;
  const canvas = document.createElement("canvas");
  const scale = targetWidth / width;
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const textItemBox = (item, viewport) => {
  if (!item?.str || !item.transform || !viewport) return null;
  const x = item.transform[4];
  const y = item.transform[5];
  const fontSize =
    Math.hypot(item.transform[2], item.transform[3]) ||
    Math.hypot(item.transform[0], item.transform[1]) ||
    item.height ||
    1;
  const width = Number(item.width) || fontSize * Math.max(item.str.length, 1) * 0.5;
  const rect = viewport.convertToViewportRectangle([
    x,
    y,
    x + width,
    y + fontSize,
  ]);
  const left = Math.min(rect[0], rect[2]) / viewport.width;
  const right = Math.max(rect[0], rect[2]) / viewport.width;
  const top = Math.min(rect[1], rect[3]) / viewport.height;
  const bottom = Math.max(rect[1], rect[3]) / viewport.height;
  if (![left, right, top, bottom].every(Number.isFinite)) return null;
  if (right <= left || bottom <= top) return null;
  return { str: item.str, left, top, right, bottom };
};

export const SheetRowPreview = ({
  url,
  storagePath = "",
  mimeType,
  fileName,
  sourcePage,
  boundingBox,
  highlightName = "",
  highlightNames = [],
  highlightAverage = null,
  highlightGames = null,
  highlightPins = null,
  containerWidth = 640,
  toolbarLeft = null,
  preferBoundingBox = false,
  cropToHighlight = false,
  fitHighlight = false,
  hideZoomBar = false,
  containerHeight = 160,
  paintHighlight = true,
}) => {
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const pageCacheRef = useRef(new Map());
  const dragRef = useRef(null);
  const lastDrawnZoomRef = useRef(null);
  const ignoreScrollRef = useRef(false);
  const zoomAnchorRef = useRef(null);
  const rootRef = useRef(null);
  const [error, setError] = useState("");
  const [pageSize, setPageSize] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [panning, setPanning] = useState(false);
  const [pdfBox, setPdfBox] = useState(null);
  const textItemsRef = useRef([]);
  const pdf = isPdfFile(mimeType, fileName);
  const overlayBox = preferBoundingBox
    ? boundingBox || pdfBox
    : pdfBox || boundingBox;
  const overlayKey = overlayBox
    ? `${highlightName}|${(highlightNames || []).join("|")}|${overlayBox.topLeftX}|${overlayBox.topLeftY}|${overlayBox.bottomRightX}|${overlayBox.bottomRightY}`
    : `${highlightName}|${(highlightNames || []).join("|")}|none`;
  const fittedKeyRef = useRef("");
  const nameHints = {
    average: highlightAverage,
    games: highlightGames,
    pins: highlightPins,
    boundingBox,
  };
  const pageNumber =
    sourcePage == null || sourcePage === ""
      ? 1
      : Math.max(1, Number(sourcePage) + 1);
  const zoomKey = pageZoomKey(storagePath, pageNumber);
  const [zoom, setZoom] = useState(() => zoomByPage.get(zoomKey) || 1);

  const changeZoom = (next) => {
    const value = clampZoom(next);
    zoomByPage.set(zoomKey, value);
    setZoom(value);
  };

  useEffect(() => {
    setZoom(zoomByPage.get(zoomKey) || 1);
  }, [zoomKey]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `${url}|${pdf ? "pdf" : "img"}|${pageNumber}`;
    const render = async () => {
      setError("");
      try {
        const targetWidth =
          Math.max(containerWidth || 280, 280) * (cropToHighlight ? 1 : zoom);
        const cached = pageCacheRef.current.get(cacheKey);
        let source = cached?.bitmap || null;
        let textItems = cached?.textItems || [];
        if (!source) {
          if (pdf) {
            const pdfjs = await loadPdfJs();
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error("Could not read this league sheet.");
            }
            const data = await response.arrayBuffer();
            const doc = await pdfjs.getDocument({ data }).promise;
            const page = await doc.getPage(
              Math.min(Math.max(pageNumber, 1), doc.numPages)
            );
            if (cancelled) return;
            const unscaled = page.getViewport({ scale: 1 });
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const scale = Math.min((1600 * dpr) / unscaled.width, 4);
            const viewport = page.getViewport({ scale });
            source = document.createElement("canvas");
            source.width = viewport.width;
            source.height = viewport.height;
            await page.render({
              canvasContext: source.getContext("2d"),
              viewport,
            }).promise;
            const content = await page.getTextContent();
            textItems = (content.items || [])
              .map((item) => textItemBox(item, viewport))
              .filter(Boolean);
          } else {
            const image = new Image();
            await new Promise((resolve, reject) => {
              image.onload = resolve;
              image.onerror = () =>
                reject(new Error("Could not show this page."));
              image.src = url;
            });
            if (cancelled) return;
            source = image;
            textItems = [];
          }
          pageCacheRef.current.set(cacheKey, { bitmap: source, textItems });
        }
        if (cancelled) return;
        textItemsRef.current = textItems;
        setPdfBox(
          pdf
            ? unionHighlightBoxes(
                (highlightNames?.length
                  ? highlightNames
                  : highlightName
                  ? [highlightName]
                  : []
                ).map((name) =>
                  findNameLineBox(textItemsRef.current, name, nameHints)
                )
              )
            : null
        );
        const size = pageSizeFromSource(source);
        const cropBox = cropToHighlight
          ? expandCropBox(parseSheetBox(overlayBox, size))
          : null;
        const drawn = cropBox
          ? drawCrop(source, containerWidth, containerHeight, cropBox)
          : drawPage(source, targetWidth);
        const canvas = canvasRef.current;
        if (!canvas || !drawn) return;
        ignoreScrollRef.current = true;
        canvas.width = drawn.width;
        canvas.height = drawn.height;
        canvas.style.width = `${drawn.width}px`;
        canvas.style.height = `${drawn.height}px`;
        canvas.getContext("2d").drawImage(drawn, 0, 0);
        setCanvasSize({ width: drawn.width, height: drawn.height });
        const parsedOverlay = parseSheetBox(overlayBox, size);
        if (parsedOverlay) {
          const ctx = canvas.getContext("2d");
          const x = parsedOverlay.topLeftX * canvas.width;
          const y = parsedOverlay.topLeftY * canvas.height;
          const w =
            (parsedOverlay.bottomRightX - parsedOverlay.topLeftX) * canvas.width;
          const h =
            (parsedOverlay.bottomRightY - parsedOverlay.topLeftY) * canvas.height;
          ctx.save();
          if (paintHighlight) {
            ctx.fillStyle = "rgba(201, 162, 39, 0.28)";
            ctx.strokeStyle = "#1a1a1a";
            ctx.lineWidth = Math.max(4, canvas.width * 0.008);
            ctx.fillRect(x, y, w, h);
            ctx.strokeRect(x, y, w, h);
            ctx.strokeStyle = "#c9a227";
            ctx.lineWidth = Math.max(3, canvas.width * 0.005);
            ctx.strokeRect(x + 2, y + 2, Math.max(1, w - 4), Math.max(1, h - 4));
          } else {
            const gold = "rgba(201, 162, 39, 0.25)";
            ctx.strokeStyle = gold;
            ctx.lineWidth = Math.max(3, canvas.width * 0.005);
            ctx.strokeRect(x + 2, y + 2, Math.max(1, w - 4), Math.max(1, h - 4));
            ctx.lineWidth = Math.max(1.5, canvas.width * 0.0025);
            ctx.strokeRect(x + 6, y + 6, Math.max(1, w - 12), Math.max(1, h - 12));
          }
          ctx.restore();
        }
        requestAnimationFrame(() => {
          if (cancelled) {
            ignoreScrollRef.current = false;
            return;
          }
          const pane = scrollRef.current;
          if (!pane) {
            ignoreScrollRef.current = false;
            return;
          }
          if (cropToHighlight) {
            ignoreScrollRef.current = false;
            return;
          }
          const previousZoom = lastDrawnZoomRef.current;
          lastDrawnZoomRef.current = zoom;
          const anchor = zoomAnchorRef.current;
          zoomAnchorRef.current = null;
          if (anchor && pane.scrollWidth > 0 && pane.scrollHeight > 0) {
            pane.scrollLeft = anchor.xRatio * pane.scrollWidth - anchor.offsetX;
            pane.scrollTop = anchor.yRatio * pane.scrollHeight - anchor.offsetY;
            savePan(zoomKey, pane);
          } else if (previousZoom && previousZoom !== zoom) {
            const ratio = zoom / previousZoom;
            pane.scrollLeft =
              (pane.scrollLeft + pane.clientWidth / 2) * ratio -
              pane.clientWidth / 2;
            pane.scrollTop =
              (pane.scrollTop + pane.clientHeight / 2) * ratio -
              pane.clientHeight / 2;
            savePan(zoomKey, pane);
          } else {
            const saved = panByPage.get(zoomKey);
            if (saved) applyPan(pane, saved);
          }
          requestAnimationFrame(() => {
            ignoreScrollRef.current = false;
          });
        });
        if (!cancelled) {
          setPageSize((current) => {
            const next = size;
            if (
              current &&
              current.width === next.width &&
              current.height === next.height
            ) {
              return current;
            }
            return next;
          });
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError("Could not show this page.");
        }
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [
    url,
    pdf,
    pageNumber,
    zoom,
    containerWidth,
    containerHeight,
    zoomKey,
    cropToHighlight,
    overlayKey,
    paintHighlight,
  ]);

  useEffect(() => {
    if (!pdf) {
      setPdfBox(null);
      return;
    }
    setPdfBox(
      pdf
        ? unionHighlightBoxes(
            (highlightNames?.length
              ? highlightNames
              : highlightName
              ? [highlightName]
              : []
            ).map((name) =>
              findNameLineBox(textItemsRef.current, name, nameHints)
            )
          )
        : null
    );
  }, [
    pdf,
    highlightName,
    highlightNames,
    highlightAverage,
    highlightGames,
    highlightPins,
    boundingBox,
    url,
    pageNumber,
  ]);

  useEffect(() => {
    lastDrawnZoomRef.current = null;
  }, [url, pageNumber]);

  useEffect(() => {
    if (cropToHighlight || !pageSize || !overlayBox) return undefined;
    const timer = window.setTimeout(() => {
      const pane = scrollRef.current;
      const highlight = pane?.querySelector("[data-sheet-highlight='true']");
      if (!pane || !highlight) return;
      ignoreScrollRef.current = true;
      const paneRect = pane.getBoundingClientRect();
      const boxRect = highlight.getBoundingClientRect();
      pane.scrollTop +=
        boxRect.top +
        boxRect.height / 2 -
        (paneRect.top + paneRect.height / 2);
      pane.scrollLeft +=
        boxRect.left +
        boxRect.width / 2 -
        (paneRect.left + paneRect.width / 2);
      savePan(zoomKey, pane);
      requestAnimationFrame(() => {
        ignoreScrollRef.current = false;
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [cropToHighlight, overlayKey, pageSize, zoomKey]);

  useEffect(() => {
    if (cropToHighlight || !fitHighlight || !pageSize || !overlayBox) {
      return undefined;
    }
    const parsed = parseSheetBox(overlayBox, pageSize);
    const pane = scrollRef.current;
    if (!parsed || !pane || pane.clientHeight < 48) return undefined;
    const fitKey = `${overlayKey}|${Math.round(pane.clientWidth)}x${Math.round(
      pane.clientHeight
    )}`;
    if (fittedKeyRef.current === fitKey) return undefined;
    const boxH = parsed.bottomRightY - parsed.topLeftY;
    const boxW = parsed.bottomRightX - parsed.topLeftX;
    if (boxH <= 0 || boxW <= 0) return undefined;
    const pageW = pageSize.width;
    const pageH = pageSize.height;
    const baseWidth = pane.clientWidth || containerWidth || 640;
    const baseHeight = baseWidth * (pageH / Math.max(pageW, 1));
    const next = clampZoom(
      Math.min(
        pane.clientHeight / (boxH * baseHeight),
        pane.clientWidth / (boxW * baseWidth)
      ) * 0.85
    );
    fittedKeyRef.current = fitKey;
    if (Math.abs(next - zoom) > 0.05) {
      changeZoom(next);
    }
    return undefined;
  }, [
    cropToHighlight,
    fitHighlight,
    overlayBox,
    overlayKey,
    pageSize,
    containerWidth,
    zoom,
  ]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || cropToHighlight) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const pane = scrollRef.current;
      const oldZoom = zoomByPage.get(zoomKey) || zoom;
      if (pane && pane.scrollWidth > 0 && pane.scrollHeight > 0) {
        const rect = pane.getBoundingClientRect();
        zoomAnchorRef.current = {
          xRatio:
            (pane.scrollLeft + event.clientX - rect.left) / pane.scrollWidth,
          yRatio:
            (pane.scrollTop + event.clientY - rect.top) / pane.scrollHeight,
          offsetX: event.clientX - rect.left,
          offsetY: event.clientY - rect.top,
        };
      }
      const direction = event.deltaY < 0 ? 1.25 : 0.8;
      changeZoom(oldZoom * direction);
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    return () => root.removeEventListener("wheel", onWheel);
  }, [cropToHighlight, zoom, zoomKey]);

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const pane = scrollRef.current;
    if (!pane) return;
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      left: pane.scrollLeft,
      top: pane.scrollTop,
    };
    setPanning(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const pane = scrollRef.current;
    if (!pane) return;
    pane.scrollLeft = dragRef.current.left - (event.clientX - dragRef.current.x);
    pane.scrollTop = dragRef.current.top - (event.clientY - dragRef.current.y);
  };

  const onPointerUp = () => {
    dragRef.current = null;
    setPanning(false);
    savePan(zoomKey, scrollRef.current);
  };

  const onScroll = () => {
    if (ignoreScrollRef.current) return;
    savePan(zoomKey, scrollRef.current);
  };

  return (
    <div ref={rootRef} className="h-full min-h-0 flex flex-col">
      {hideZoomBar || cropToHighlight ? null : (
      <ZoomBar
        zoom={zoom}
        onZoomOut={() => changeZoom(zoom / 1.25)}
        onZoomIn={() => changeZoom(zoom * 1.25)}
        onReset={() => changeZoom(1)}
        toolbarLeft={toolbarLeft}
      />
      )}
      {error ? (
        <p className="text-sm text-brand-muted mb-2 shrink-0">{error}</p>
      ) : null}
      <div
        ref={scrollRef}
        className={`flex-1 min-h-0 h-0 overscroll-contain select-none ${
          cropToHighlight
            ? "overflow-hidden flex items-center justify-center"
            : `overflow-auto touch-none ${panning ? "cursor-grabbing" : "cursor-grab"}`
        }`}
        onPointerDown={cropToHighlight ? undefined : onPointerDown}
        onPointerMove={cropToHighlight ? undefined : onPointerMove}
        onPointerUp={cropToHighlight ? undefined : onPointerUp}
        onPointerCancel={cropToHighlight ? undefined : onPointerUp}
        onLostPointerCapture={cropToHighlight ? undefined : onPointerUp}
        onScroll={cropToHighlight ? undefined : onScroll}
      >
        <div
          className="relative select-none"
          style={{
            width: canvasSize.width || undefined,
            height: canvasSize.height || undefined,
          }}
        >
          <canvas ref={canvasRef} className="block max-w-none pointer-events-none" />
          {cropToHighlight ? null : (
            <HighlightBox
              box={overlayBox}
              pageSize={pageSize}
              width={canvasSize.width}
              height={canvasSize.height}
              paintHighlight={paintHighlight}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SheetRowPreview;
